import { Button } from '@invent/wl-ui-kit-next/ui';
import { UsersRound } from 'lucide-react';
import * as React from 'react';
import {
  ChoiceSelect,
  CondensedTip,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useCondensed
} from '@/design';
import { listsApi } from '../../api/lists-api';
import type { IListAssignee, IListEditCellRef, IListQuery } from '../../api/lists-contracts';
import { countOf } from '../../lists';
import { refusalMessage } from '../../refusal';
import { MASS_UPDATE_LIMIT } from './actions';
import { ASSIGNED_TO } from './editing';
import { rowKeyOf } from './use-list-rows';

/**
 * *Assign all N…* (SR-459): every row a query matches assigned to one account, or to nobody — the
 * Lists' mass update `{query, set: [sys.assigned_to]}`, which survey-api takes under `lists.assign`
 * when the assignee is all it sets. Not DD's: the caller hands the query its list reads, the count
 * that read answered and the noun its rows are, so any queue over Lists can draw it.
 *
 * **Past 2,000 rows nothing is sent** (survey-api's `MassUpdateLimit`): the dialog says so off the
 * caller's count, and survey-api's 400 at `query` is said in its words all the same, since the list
 * may have grown since. **Undo puts each row back as the answer found it** — the cells' form, each
 * applied cell's `previousAssignee` (absent: nobody) at the version the answer gave — so a row a
 * colleague moved since comes back a conflict and stays theirs.
 */

export interface INoun {
  one: string;
  many: string;
}

/** What one *Assign all* wrote — what `AssignAllSaid` says and undoes. */
export interface IAssignedAll {
  /** The view the write went under, which the undo goes under too. */
  viewId?: Nullable<string>;
  /** The account assigned, or null for nobody. */
  accountId: Nullable<string>;
  /** The account's address, or null for nobody. */
  whom: Nullable<string>;
  applied: IListEditCellRef[];
  unchanged: number;
  conflicts: number;
}

/** An assignee cell a write left: the row's ref, and the account it now holds (null: nobody). */
export interface IAssignedCell {
  ref: IListEditCellRef;
  accountId: Nullable<string>;
}

/** The choice that clears the assignee: no account id spells it. */
export const NOBODY = 'nobody';

/** An outcome's clauses, the ones that hold, as one sentence. */
export function sentence(parts: ReadonlyArray<string | false>): string {
  return parts.filter(Boolean).join('; ') + '.';
}

export function AssignAll({
  query,
  viewId,
  count,
  noun,
  accounts,
  disabled,
  describedBy,
  onAssigned
}: {
  /** The list's query as its rows are read — the scope, the criteria — sent as the mass update's. */
  query: IListQuery;
  viewId?: Nullable<string>;
  /** The rows the query matched at the list's last read; null while unread. */
  count: Nullable<number>;
  noun: INoun;
  accounts: Nullable<IListAssignee[]>;
  disabled: boolean;
  /** The id of the sentence saying why the press is off. */
  describedBy?: string;
  /** The write landed: say it, and read the list again. */
  onAssigned: (done: IAssignedAll) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [whom, setWhom] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [refused, setRefused] = React.useState<Nullable<string>>(null);
  const labelId = React.useId();
  const { condensed } = useCondensed();
  const n = count ?? 0;
  const tooMany = n > MASS_UPDATE_LIMIT;
  const email = accounts?.find((account) => account.accountId === whom)?.email ?? null;
  const rows = countOf(n, noun.one, noun.many);

  const assign = async () => {
    setBusy(true);
    setRefused(null);
    try {
      const accountId = whom === NOBODY ? null : whom;
      const { viewId: _ignored, columns: _drawn, ...matched } = query;
      const answer = await listsApi.edit({ viewId, query: matched, set: [{ field: ASSIGNED_TO, value: accountId }] });
      setOpen(false);
      setWhom('');
      onAssigned({
        viewId,
        accountId,
        whom: accountId === null ? null : email,
        applied: answer.applied,
        unchanged: answer.unchanged.length,
        conflicts: answer.conflicts.length
      });
    } catch (error) {
      setRefused(refusalMessage(error, 'The rows could not be assigned.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <CondensedTip label={`Assign all ${n.toLocaleString('en-US')}…`}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={condensed ? 'size-8 p-0' : undefined}
          aria-label={condensed ? `Assign all ${n.toLocaleString('en-US')}…` : undefined}
          disabled={disabled || n === 0}
          aria-describedby={describedBy}
          onClick={() => {
            setRefused(null);
            setOpen(true);
          }}
        >
          {condensed ? <UsersRound className="size-4" aria-hidden /> : <>Assign all {n.toLocaleString('en-US')}…</>}
        </Button>
      </CondensedTip>
      <Dialog open={open} onOpenChange={(next) => !busy && setOpen(next)}>
        <DialogContent data-assign-all-dialog="">
          <DialogHeader>
            <DialogTitle>Assign all {rows}</DialogTitle>
            <DialogDescription>
              {whom === ''
                ? `The filter holds ${rows}.`
                : whom === NOBODY
                  ? `Unassign ${rows}?`
                  : `Assign ${rows} to ${email ?? 'the account chosen'}?`}
            </DialogDescription>
          </DialogHeader>
          <DialogBody
            as="form"
            onSubmit={(event) => {
              event.preventDefault();
              if (whom !== '' && !tooMany) void assign();
            }}
          >
            <div className="grid gap-1.5 text-sm">
              <span id={labelId} className="font-medium">
                Assign to
              </span>
              <ChoiceSelect
                labelledBy={labelId}
                value={whom}
                onChange={setWhom}
                disabled={busy}
                choices={[
                  { value: NOBODY, label: 'Nobody' },
                  ...(accounts ?? []).map((account) => ({ value: account.accountId, label: account.email }))
                ]}
              />
            </div>
            {tooMany && (
              <p role="alert" className="text-sm text-destructive">
                Narrow the filter: {rows} match, and one assignment takes at most{' '}
                {MASS_UPDATE_LIMIT.toLocaleString('en-US')}.
              </p>
            )}
            {refused && (
              <p role="alert" className="text-sm text-destructive">
                {refused}
              </p>
            )}
            <DialogFooter className="px-0 pb-0">
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={busy || whom === '' || tooMany}>
                {busy ? 'Assigning…' : 'Assign'}
              </Button>
            </DialogFooter>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * An *Assign all*'s outcome above its list, with *Undo* — pressed once — and *Dismiss*. Kept until
 * dismissed or undone: it names rows the re-read may no longer draw.
 */
export function AssignAllSaid({
  done,
  noun,
  onChanged,
  onDismiss
}: {
  done: IAssignedAll;
  noun: INoun;
  /** After the undo's write, with the cells it put back: read the list again. */
  onChanged: (restored: IAssignedCell[]) => void;
  onDismiss: () => void;
}) {
  const [undoing, setUndoing] = React.useState(false);
  const [undone, setUndone] = React.useState<Nullable<string>>(null);
  const [failed, setFailed] = React.useState<Nullable<string>>(null);
  const whom = done.whom ?? 'nobody';

  const undo = async () => {
    setUndoing(true);
    setFailed(null);
    try {
      const answer = await listsApi.edit({
        viewId: done.viewId,
        note: 'Undone straight after assigning all',
        cells: done.applied.map((cell) => ({
          participantId: cell.participantId,
          survey: cell.survey,
          field: ASSIGNED_TO,
          value: cell.previousAssignee ?? null,
          version: cell.version
        }))
      });
      const back = answer.applied.length + answer.unchanged.length;
      const kept = answer.conflicts.length;
      setUndone(
        sentence([
          `Undone: ${countOf(back, noun.one, noun.many)} back as they were`,
          kept > 0 && `${kept.toLocaleString('en-US')} left as a colleague moved them since`
        ])
      );
      const before = new Map(done.applied.map((cell) => [rowKeyOf(cell), cell.previousAssignee ?? null]));
      onChanged(answer.applied.map((ref) => ({ ref, accountId: before.get(rowKeyOf(ref)) ?? null })));
    } catch (error) {
      setFailed(refusalMessage(error, 'The assignment could not be undone.'));
      setUndoing(false);
    }
  };

  return (
    <div
      data-assign-all=""
      className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-[color-mix(in_srgb,var(--color-teal)_8%,white)] px-3 py-2 text-[0.8125rem]"
    >
      <span role="status">
        {undone ??
          sentence([
            `${countOf(done.applied.length, noun.one, noun.many)} assigned to ${whom}`,
            done.unchanged > 0 && `${done.unchanged.toLocaleString('en-US')} already ${done.whom ? 'theirs' : 'unassigned'}`,
            done.conflicts > 0 && `${done.conflicts.toLocaleString('en-US')} left as a colleague set them`
          ])}
      </span>
      {failed && <span role="alert">{failed}</span>}
      {undone === null && done.applied.length > 0 && (
        <Button type="button" variant="outline" size="sm" disabled={undoing} onClick={() => void undo()}>
          Undo
        </Button>
      )}
      <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}
